import { ListingProduct } from '@rdss/client'
import { toDecimal } from '@rdss/core'
import { Component, Host, JSX, Prop, State, h, Element, Event, EventEmitter, Build, Listen } from '@stencil/core'
import { trans, store, addToCart } from '~/store'
import { isSponsoredProduct, trackCitrusAd } from '~/utils/tracking'
import { IntersectionOptions } from './types'

const IntersectionOptions: IntersectionOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 1.0,
}

@Component({
    tag: 'zr-product-tile',
    styleUrl: 'zr-product-tile.scss',
    shadow: true,
})

/**
 * ZooRoyal price component.
 */
export class ZrProductTile {
    /**
     * Product.
     */
    @Prop() product: ListingProduct

    /**
     * Styling context extra.
     */
    @Prop() context: 'product-category' | 'shopping-worlds' | 'showroom-ad' = 'product-category'

    /**
     * Translations.
     */
    @Prop() translations: { [key: string]: string }

    /**
     * Use loyalty points.
     */
    @Prop() useLoyaltyPoints = false

    /**
     * Show add to cart.
     */
    @Prop() showAddToCart = true

    /**
     * Disable add to cart.
     */
    @Prop() disabled = false

    /**
     * Product sold out.
     */
    @Prop() soldOut = trans(
        'frontend/detail/product-variants.ausverkauft',
        'Ausverkauft')

    /**
     * Is the procut-tile hovered.
     */
    @State() isProductTileHovered = false

    /**
     * Is the procut-tile focused.
     */
    @State() isProductTileFocused = false

    /**
     * Show tooltip.
     */
    @State() showTooltip = false

    /**
     * Tooltip-text.
     */
    @State() toolTipText = trans(
        'frontend/listing/addtocart.addToCartTooltip',
        'Hinzugefügt'
    )

    /**
     * Api response for the add-to-cart tooltip.
     */
    @State() toolTipApiResponse = true

    /**
     * Show tooltip icon in add-to-cart tooltip.
     */
    @State() toolTipShowIcon = true

    /**
     * Disable tooltip.
     */
    @State() showDisabledTooltip = false

    /**
     * The host element to reference component elements inside the component.
     */
    @Element() host: HTMLElement

    /**
     * Event which is emitted if the product tile is clicked.
     */
    @Event() productClicked: EventEmitter<{ product: ListingProduct }>

    /**
     * Event which is emitted if the product tile enters the viewport.
     */
    @Event() enteredViewport: EventEmitter<{ product: ListingProduct }>

    /**
     * Event emitted when the add to cart button is clicked.
     */
    @Event() addToCartClicked: EventEmitter<{ quantity: number, orderNumber?: string, context?: string }>

    /**
     * Refresh member points if product is loyalty item.
     */
    @Event() refreshMemberPoints: EventEmitter

    /**
     * The product tile anchor element.
     */
    private productTile: HTMLElement

    /**
     * The product tile anchor element.
     */
    private productTileElement: HTMLAnchorElement

    /**
     * The rating anchor element.
     */
    private ratingElement: HTMLAnchorElement

    /**
     * The price anchor element.
     */
    private priceElement: HTMLAnchorElement

    /**
     * Timeout for tooltip.
     */
    private showHideTimeout: ReturnType<typeof setTimeout> | null = null

    /**
     * Mouse hover event.
     */
    private mouseEnterEvent = () => {
        this.isProductTileHovered = true
    }

    /**
     * Mouse hover event.
     */
    private mouseLeaveEvent = () => {
        this.isProductTileHovered = false
    }

    /**
     * Scroll into view.
     */
    scrollIntoView() {
        this.productTile.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    /**
     * Focus event.
     */
    focusEvent() {
        if (!this.isProductTileFocused) {
            this.scrollIntoView()
        }
        this.isProductTileFocused = true
    }

    /**
     * Blur event.
     */
    private blurEvent = () => {
        this.isProductTileFocused = false
    }

    /**
     * Handles Product Click.
     */
    async handleProductClicked() {
        let productUrl = this.product.seoUrl
        if (isSponsoredProduct(this.product)) {
            await trackCitrusAd('click', this.product)
            productUrl = productUrl.includes('?') ? `${productUrl}&` : `${productUrl}?`
            productUrl = `${productUrl}sponsored=${this.product.citrusAdId}`
        }
        if (this.product?.seoUrl) {
            window.location.href = productUrl
        }
    }

    /**
     * Event which is emitted if the product tile is clicked.
     */
    private onProductClicked = () => {
        this.handleProductClicked()
        this.productClicked.emit({ product: this.product })
    }

    /**
     * Creates Intersection Observer to detect if the element enters the viewport
     * Intersection Obeserver callback which is called if the visibility of one element changed.
     */
    private handleProductVisibility() {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return

                this.enteredViewport.emit({ product: this.product })
            })
        }, IntersectionOptions)
        observer.observe(this.host)
    }

    /**
     * Returns the product link checking for sponsored products.
     */
    private productLink() {
        let url = this.product.seoUrl
        if (isSponsoredProduct(this.product)) {
            url = url.includes('?') ? `${url}&` : `${url}?`
            url = `${url}sponsored=${this.product.citrusAdId}`
        }
        return url
    }

    /**
     * Returns the aria description for the rating icon.
     */
    private ratingDescription() {
        const ratingCount = this.product.rating.count === 1
            ? trans('frontend/listing/listing_messages.ariaRatingCountDescriptionSingular',
                'von einer Bewertung.')
            : trans('frontend/listing/listing_messages.ariaRatingCountDescription',
                'von %s Bewertungen.', [this.product.rating.count.toString()])
        return `${trans('frontend/listing/listing_messages.ariaRatingStarsDescription',
            '%s von 5 Sternen', [toDecimal(this.product.rating.average / 2)])} ${ratingCount}`
    }

    /**
     * Handle component load.
     */
    async componentWillLoad() {
        this.handleProductVisibility()
    }

    /**
     * Lifecycle method to set the margin bottom and border radius.
     */
    componentDidLoad() {
        if (Build.isBrowser) {
            this.host.style.setProperty('--zr-product-tile-sponsored-blur', 'none')
            if (!this.product?.sponsored) {
                this.host.classList.remove('zr-product-tile__sponsored')
                this.host.shadowRoot.querySelector('article')?.classList.remove('zr-product-tile__sponsored')
            }
        }
    }

    /**
     * Handle keyboard navigation.
     */
    private handleKeyNavigation(event: KeyboardEvent) {
        if(event.key === 'Escape') {
            this.productTileElement.focus()
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            if (event.target === this.ratingElement) {
                if (this.priceElement) {
                    this.priceElement.focus()
                }
            } else {
                if (this.ratingElement) {
                    this.ratingElement.focus()
                } else if (this.priceElement && event.target !== this.priceElement) {
                    this.priceElement.focus()
                }
            }

        }
    }

    /**
     * Check if the product has no variants and sponsored products.
     * @private
     */
    private shouldDisplayAddToCartCount() {
        return (
            this.product?.availableVariants === 1 ||
                isSponsoredProduct(this.product) ||
                this.useLoyaltyPoints
        ) &&
            this.showAddToCart &&
            !this.hasWarningOrInfo()
    }

    /**
     * Check if the product has variants or warning info and is not a sponsored product.
     * @private
     */
    private shouldDisplayAddToCartOverlay() {
        return (
            this.product?.availableVariants > 1 &&
            !isSponsoredProduct(this.product) &&
            !this.useLoyaltyPoints &&
            this.showAddToCart
        ) ||
            this.hasWarningOrInfo()
    }

    /**
     * Check if the product has Warning Info.
     * @private
     */
    private hasWarningOrInfo() {
        return this.product?.isBiocide || this.product?.isVeterinary
    }

    /**
     * Checks if product is in cart.
     */
    getCartQuantity(sku: string): number {
        const item = store.state.cart?.items?.find(obj => obj.sku === sku)
        return item?.quantity || 0
    }

    /**
     * Adds the product to the cart.
     */
    @Listen('handleQuantityChange')
    async addCartItem({ detail } : CustomEvent<{ quantity: number, productId: number, context: string, increased: boolean }>) {
        const payload = {
            quantity: detail.quantity,
            orderNumber: detail.productId.toString(),
            context: detail.context,
            useLoyaltyPoints: this.useLoyaltyPoints,
        }
        this.addToCartClicked.emit(payload)
        const success = await addToCart(payload, this.product);

        if(detail.increased) {
            if(success){
                this.toolTipText = trans(
                    'frontend/listing/addtocart.addToCartTooltip',
                    'Hinzugefügt'
                )
            }else{
                this.toolTipText = trans(
                    'frontend/listing/addtocart.addToCartTooltipFailed',
                    'Nicht hinzugefügt!'
                )
                this.toolTipApiResponse = false
                this.toolTipShowIcon = false
            }
            this.showTooltip = true
            clearTimeout(this.showHideTimeout)
            this.showHideTimeout = setTimeout(() => {
                this.showTooltip = false
            }, 2000)
        }
        if(success){
            this.refreshMemberPoints.emit();
        }
    }

    /**
     * Show tooltip if add to cart is disabled.
     */
    disabledAddToCart() {
        if(this.showTooltip) {
            return
        }

        if(this.disabled || !this.product.stock) {
            this.showDisabledTooltip = true
            clearTimeout(this.showHideTimeout)
            this.showHideTimeout = setTimeout(() => {
                this.showDisabledTooltip = false
            }, 2000)
        }
    }

    /**
     * Returns the aria label for the product tile header.
     */
    getAriaLabelHeader() {
        return `${this.product.name} ${this.product.configurationText} ${this.useLoyaltyPoints
            ? this.product.price?.loyaltyPoints + ' Punkte'
            : this.product.price?.gross + ' ' + this.product.price?.currency}
            ${!this.product.stock ? this.soldOut : ''}`
    }

    /**
     * Renders the component.
     */
    render(): JSX.Element {
        if (this.product) {
            const {
                name,
                imageUrl,
                brand,
                availableVariants,
                locale,
                rating,
                price,
                badges,
                configurationText,
                sponsored,
                sku,
                variants,
                stock,
            } = this.product

            return (
                <Host onClick={this.onProductClicked}
                    class={{ 'zr-product-tile__sponsored': sponsored }}
                    ref={el => (this.productTile = el)}
                >
                    <article
                        class={{
                            'zr-product-tile': true,
                            'zr-product-tile__focused': this.isProductTileFocused,
                            'zr-product-tile__sponsored': sponsored,
                        }}
                        onMouseEnter={this.mouseEnterEvent.bind(this)}
                        onMouseLeave={this.mouseLeaveEvent.bind(this)}
                        part="tile"
                    >
                        <div class="zr-product-tile__image">
                            {(badges?.length >= 1) && (
                                <zr-badges badges={this.product.badges} discount={price.pseudoPercent} />
                            )}
                            <vx-picture
                                src={imageUrl}
                                alt={this.product.name}
                                width={236}
                                height={236}
                                fit="scale-down"
                            />
                        </div>

                        <div class="zr-product-tile__detail">
                            <a
                                href={this.productLink()}
                                onFocus={this.focusEvent.bind(this)}
                                onBlur={this.blurEvent.bind(this)}
                                ref={el => (this.productTileElement = el)}
                                onClick={ev => ev.preventDefault()}
                                onKeyDown={this.handleKeyNavigation.bind(this)}
                                role="link"
                            >
                                <div
                                    class="zr-product-tile__supplier"
                                    aria-hidden="true">
                                    {brand}</div>
                                <h3 class="zr-product-tile__name"
                                    innerHTML={name}
                                    aria-label={this.getAriaLabelHeader()}
                                />
                            </a>
                            {!this.useLoyaltyPoints &&
                                <div class="zr-product-tile__current-variant">
                                    {configurationText}&#8203;
                                    {(!isSponsoredProduct(this.product) && availableVariants > 1) && (
                                        <span class="zr-product-teaser__variants">
                                        &#8203;&nbsp;+ {availableVariants - 1} {trans(
                                                availableVariants === 2
                                                    ? 'frontend/detail/product-variants.DetailProductvariantsVariant'
                                                    : 'frontend/detail/product-variants.DetailProductvariantsVariants',
                                                availableVariants === 2 ? 'Variante' : 'Varianten'
                                            )}
                                        </span>
                                    )}
                                </div>
                            }
                            {(!this.useLoyaltyPoints && (rating?.average && rating?.count)) ? (
                                <a
                                    tabindex="-1"
                                    class="zr-product-tile__rating"
                                    ref={el => (this.ratingElement = el)}
                                    onKeyDown={this.handleKeyNavigation.bind(this)}
                                    href={this.productLink()}
                                    onFocus={this.focusEvent.bind(this)}
                                    onBlur={this.blurEvent.bind(this)}
                                    onClick={ev => ev.preventDefault()}
                                    role="link"
                                    aria-label={this.ratingDescription()}
                                    part="rating"
                                >
                                    <zr-rating-badge
                                        class="zr-rating-badge--listing"
                                        value={rating.average}
                                        count={rating.count}
                                        hideCount={true}
                                        locale={locale}
                                    />
                                </a>

                            ) : (<div part="rating"></div>)}
                            {!this.useLoyaltyPoints &&
                            <a
                                tabindex="-1"
                                class="zr-product-tile__price"
                                ref={el => (this.priceElement = el)}
                                onKeyDown={this.handleKeyNavigation.bind(this)}
                                href={this.productLink()}
                                onFocus={this.focusEvent.bind(this)}
                                onBlur={this.blurEvent.bind(this)}
                                onClick={ev => ev.preventDefault()}
                                role="link"
                            >
                                <zr-price
                                    class={`zr-price--${this.context}`}
                                    pseudoPercent={price?.pseudoPercent}
                                    recommendedRetailPrice={price?.grossPseudo}
                                    gross={price?.gross}
                                    locale={locale}
                                    currency={price?.currency}
                                    weightUnit={price?.unit}
                                    totalPriceLabel={trans(
                                        'frontend/detail/index/priceCalcTotal',
                                        'Gesamtpreis'
                                    )}
                                    isPriceHighlight={this.isProductTileHovered || this.isProductTileFocused}
                                    referenceUnit={price?.referenceUnit?.toString()}
                                    referencePriceUnit={price?.pricePerUnit}
                                />
                            </a>
                            }
                            <div class={{
                                'zr-product-tile__add-to-cart': true,
                                'zr-product-tile__add-to-cart--use-points': this.useLoyaltyPoints,
                            }}
                            onClick={(ev) => {
                                ev.stopPropagation()
                                ev.preventDefault()
                            }}>
                                {this.useLoyaltyPoints &&
                                    <zr-loyalty-points-badge
                                        points={this.product.price?.loyaltyPoints}
                                    />
                                }
                                {(this.shouldDisplayAddToCartOverlay()) && (
                                    <zr-add-to-cart-overlay
                                        variants={variants}
                                        productId={sku}
                                        listingProduct={this.product}
                                        isSponsored={sponsored}
                                        variantsInCartLabel={trans(
                                            'frontend/detail/product-variants.VariantsInCart',
                                            'Artikel versch. Varianten im Warenkorb.'
                                        )}
                                        context={`${this.context}-overlay`}
                                    />
                                ) || (this.shouldDisplayAddToCartCount()) && (
                                    <div>
                                        {(this.showTooltip) && (
                                            <zr-tooltip
                                                text={this.toolTipText}
                                                apiResponse={this.toolTipApiResponse}
                                                showIcon={this.toolTipShowIcon}
                                            />
                                        )}
                                        {(this.showDisabledTooltip && !this.useLoyaltyPoints) && (
                                            <zr-tooltip
                                                text={this.soldOut}
                                                apiResponse={!this.toolTipApiResponse}
                                                showIcon={!this.toolTipShowIcon}
                                                disabledButton={this.showDisabledTooltip}
                                            />
                                        )}
                                        <zr-add-to-cart-count
                                            maxQuantity={Math.min(100, stock)}
                                            cartQuantity={this.getCartQuantity(sku)}
                                            productId={sku}
                                            disabled={this.disabled}
                                            context={`${this.context}-list`}
                                            onClick={() => this.disabledAddToCart()}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </article>
                </Host>
            )
        }
    }
}
